import _ from 'lodash'
import { textCO2e } from '../components/Typography'
import { FilterInput, RefernceValueFilter } from '../graphql/generated'

type FormatOptions = {
  co2ePostfix?: boolean
  hideUnit?: boolean
  showSeparateSign?: boolean
  filter?: FilterInput
}

// Given a string type, or a union type of strings:
// Create the type that contains the original string(s),
// and the original string postfixed with " / kr".
// E.g.
// type X = StrWithOptKrSuffix<'foo' | 'bar'>
// is the same as
// type X = 'foo' | 'foo / kr' | 'bar' | 'bar / kr'
type StrWithOptKrSuffix<S extends string> = `${S}${'' | ' / kr'}`

type MassUnit = 'g' | 'kg' | 'ton' | 'kton' | 'Mton'

export type NumberFormatter = {
  // Unit is either emissions in mass of CO2e, in e.g. "kg",
  // or emission intensity, for example per revenue, e.g. "g / kr" (implicit "g CO2e / kr")
  unit: StrWithOptKrSuffix<MassUnit>
  /**
   * The function used to round the given value when formatting with {@link NumberFormatter.format}.
   *
   * The rounding depends on the options, and may for example:
   * - return the same value (for kg)
   * - return 1 / 1000 of the value (for ton)
   *
   * @param x The value to round
   * @returns The rounded value
   */
  round: (x: number) => number
  /**
   * Format a given value according to formatting options.
   *
   * The options are normally initialised in {@link getNumberFormatter}, but can be overridden.
   *
   * The function returns a string with normalised roudning.
   *
   * @param val The number to format
   * @param optionsOverride (optional) Override of the options
   * @returns The value formatted as a string, per the options
   */
  format: (val: number, optionsOverride?: FormatOptions) => string
}

/**
 * Get a formatting utility set for the given value.
 *
 * The returned utilities adapts to the given options.
 *
 * Normally, the values in `vals` are assumed to represent kg of CO2e.
 * An exception is if the `options.filter.referenceValue` is "Revenue",
 * in which case the values are assumed to represent "g CO2e / kr".
 *
 * @param vals
 * @param options
 * @returns An object with formattin utilities
 */
export const getNumberFormatter = (
  vals: number[],
  options?: FormatOptions,
): NumberFormatter => {
  const res: NumberFormatter = {
    round: (x: number) => Math.round(x),
    unit: 'kg',
    format: () => '',
  }

  const maxval = Math.abs(_.max(vals) ?? 1e4)

  // Get a format option, using the supplied options if available, otherwise falling back to this formatter's options.
  const getOption = <K extends keyof FormatOptions>(
    k: K,
    op?: FormatOptions,
  ): FormatOptions[K] | undefined => op?.[k] ?? options?.[k]

  // Locale used for cultural context when formatting numbers.
  const localeCulture = 'sv' // TODO: Read from user preferences?

  // Internal formatter for numeric value: captures behaviour such as culture and number of fractional digits.
  const numberFormatter: {
    numberFormat: (
      x: number,
      overrideOpt?: FormatOptions,
      intlOpts?: Intl.NumberFormatOptions,
    ) => string
  } = {
    numberFormat: () => '',
  }

  // NOTE: This captures 'res' and uses res.round -- make sure to set res.round before calling.
  const format_value = (
    x: number,
    overrideOpt?: FormatOptions,
    intlOpts?: Intl.NumberFormatOptions,
  ) => {
    const valsign = x >= 0 ? '+' : '-'
    // When a prefixed sign should be displayed, the normal sign formatting must be disabled;
    // Prevent double minus-signs when forcing a prefix-sign, by using the absolute value for display.
    const show_sign = getOption('showSeparateSign', overrideOpt)
    const prefix_sign = show_sign ? `${valsign} ` : ''
    const v = show_sign ? Math.abs(x) : x
    return `${prefix_sign}${res
      .round(v)
      .toLocaleString(localeCulture, intlOpts)}`
  }

  if (maxval < 1e3) {
    res.unit = 'kg'
    if (options?.filter?.referenceValue === RefernceValueFilter.Revenue) {
      // Revenue means intensity of emissions per revenue, which is in "g / kr".
      res.unit = 'g / kr'
    }
    res.round = (val) => val
    numberFormatter.numberFormat = (x: number, overrideOpt?: FormatOptions) =>
      format_value(x, overrideOpt, {
        // If displaying revenue, i.e. "g / kr", we want 2 digits of precision,
        // otherwise we are displaying "kg", and want 0 digits.
        maximumFractionDigits:
          options?.filter?.referenceValue === RefernceValueFilter.Revenue
            ? 2
            : 0,
      })
  } else if (maxval < 1e6) {
    res.unit = 'ton'
    if (options?.filter?.referenceValue === RefernceValueFilter.Revenue) {
      // Units of this magnitude or higher are not expected for intensity,
      // but units are included for consistency.
      res.unit = 'kg / kr'
    }
    res.round = (val) => val / 1e3
    numberFormatter.numberFormat = (x: number, overrideOpt?: FormatOptions) =>
      format_value(x, overrideOpt, {
        maximumFractionDigits:
          options?.filter?.referenceValue === RefernceValueFilter.Revenue
            ? 2
            : 1,
        minimumFractionDigits: 1,
      })
  } else if (maxval < 1e9) {
    res.unit = 'kton'
    if (options?.filter?.referenceValue === RefernceValueFilter.Revenue) {
      res.unit = 'ton / kr'
    }
    res.round = (val) => val / 1e6
    numberFormatter.numberFormat = (x: number, overrideOpt?: FormatOptions) =>
      format_value(x, overrideOpt, {
        maximumFractionDigits:
          options?.filter?.referenceValue === RefernceValueFilter.Revenue
            ? 2
            : 1,
        minimumFractionDigits: 1,
      })
  } else {
    res.unit = 'Mton'
    if (options?.filter?.referenceValue === RefernceValueFilter.Revenue) {
      res.unit = 'kton / kr'
    }
    res.round = (val) => val / 1e9
    numberFormatter.numberFormat = (x: number, overrideOpt?: FormatOptions) =>
      format_value(x, overrideOpt, {
        maximumFractionDigits:
          options?.filter?.referenceValue === RefernceValueFilter.Revenue
            ? 2
            : 1,
        minimumFractionDigits: 1,
      })
  }

  const unit = (overrideOpt?: FormatOptions) =>
    getOption('hideUnit', overrideOpt) ? '' : ` ${res.unit}`
  const postfix = (overrideOpt?: FormatOptions) =>
    getOption('co2ePostfix', overrideOpt) ? ` ${textCO2e}` : ''

  res.format = (val, overrideOpt) =>
    `${numberFormatter.numberFormat(val, overrideOpt)}${unit(
      overrideOpt,
    )}${postfix(overrideOpt)}`

  return res
}

type UnitDescriptionOptions = {
  hideUnit?: boolean
}
export const unitDescription = (
  filter: FilterInput,
  options?: UnitDescriptionOptions,
): string => {
  let suffix = ''
  if (filter.referenceValue === RefernceValueFilter.AnnualWorkForce) {
    suffix = `${suffix} / anställd`
  } else if (filter.referenceValue === RefernceValueFilter.Revenue) {
    // Revenue is "(mass of) CO2e / kr".
    suffix = `${suffix} / kr omsättning`
  }

  const refValFilterNotSet =
    !filter.referenceValue || filter.referenceValue === RefernceValueFilter.None

  if (filter.partialYear) {
    // Intensity per revenue cannot be displayed for the partial year
    // (at the time of writing at least; we may support partial year revenue at some point)
    // so do not modify the string for that case.
    if (filter.referenceValue === RefernceValueFilter.AnnualWorkForce) {
      suffix = `${suffix} (snitt delår)`
    } else if (refValFilterNotSet) {
      suffix = `${suffix} (totalt delår)`
    }
  } else if (!filter.year) {
    if (!refValFilterNotSet) {
      // TODO: Check if this condition occurs:
      // The combination of year not being set and a ref value being used
      // implies some kind of aggregation over all years for that ref value.
      // I don't think this is used anywhere -- maybe it was at some point?
      // Either way, this text of "mean value for all years" would probably be
      // what we want for both annual work force and intensity, if they were displayed.
      suffix = `${suffix} (snitt alla år)`
    } else {
      suffix = `${suffix} (totalt alla år)`
    }
  }

  return `${!options?.hideUnit ? textCO2e : ''}${suffix}`
}
